import { useEffect, useState, useMemo, useContext, useRef, useCallback } from "react";
import { getApiUrl } from "../utils/api";
import Modal from "../components/Modal";
import { compressImageUpload } from "../utils/imageCompression";
import { useNavigate } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import { UserContext } from "../context/UserContext";
import {
  FaUser,
  FaUsers,
  FaBook,
  FaUserShield,
  FaUserSlash,
  FaCheck,
  FaTimes,
  FaEye,
  FaSync,
  FaSignOutAlt,
  FaTrash,
  FaBullhorn,
  FaCalendarAlt,
  FaUserCircle,
  FaClock,
  FaPlus,
  FaNewspaper,
  FaImage,
  FaFileImage,
  FaSpinner,
  FaHeart,
  FaComment,
  FaChartBar,
  FaCircle,
  FaEllipsisV,
  FaSlidersH,
} from "react-icons/fa";
import AdminShell from "../components/admin/AdminShell";
import "./AdminDashboard.css";
import { getAvatarUrl } from "../utils/avatar";
import { convertFromRaw } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { Line } from "react-chartjs-2";
import Cropper from "react-easy-crop";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);

function AdminDashboard() {
  const { user: currentUser } = useContext(UserContext);
  const isSuperAdmin = currentUser?.role === "superadmin";
  const [users, setUsers] = useState([]);
  const [poems, setPoems] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [userPage, setUserPage] = useState(1);
  const [poemPage, setPoemPage] = useState(1);
  const [openUserMenu, setOpenUserMenu] = useState(null); // { id, top, right }
  const userMenuRef = useRef(null);
  const [usersPagination, setUsersPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
  });
  const [poemsPagination, setPoemsPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
  });
  const USERS_PER_PAGE = 10;
  const POEMS_PER_PAGE = 10;
  const [userSearch, setUserSearch] = useState("");
  const [poemSearch, setPoemSearch] = useState("");
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const navigate = useNavigate();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [poemEdit, setPoemEdit] = useState({
    open: false,
    poem: null,
    title: "",
    content: "",
  });
  const [reportModal, setReportModal] = useState({ open: false, poem: null });
  const [poemFilters, setPoemFilters] = useState({
    author: "",
    date: "",
    report: "",
    featured: "",
  });
  const editContentRef = useRef();
  const notificationInputRef = useRef(null);
  const [growthStats, setGrowthStats] = useState(null);
  const [engagementStats, setEngagementStats] = useState(null);
  const [activeUsers, setActiveUsers] = useState(null);
  const [adminMods, setAdminMods] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    message: "",
    expiresAt: "",
  });
  const [notifyModal, setNotifyModal] = useState({
    open: false,
    user: null,
    message: "",
    type: "info",
  });
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
  });
  const pendingConfirmRef = useRef(null);

  const closeConfirmModal = useCallback(() => {
    pendingConfirmRef.current = null;
    setConfirmModal({ open: false, title: "", message: "" });
  }, []);

  useEffect(() => {
    if (!openUserMenu) return undefined;
    const onPointerDown = (e) => {
      const btn = e.target.closest?.("[data-user-menu-btn]");
      if (btn && String(btn.getAttribute("data-user-menu-btn")) === String(openUserMenu.id)) {
        return;
      }
      if (userMenuRef.current?.contains(e.target)) return;
      setOpenUserMenu(null);
    };
    const onClose = () => setOpenUserMenu(null);
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [openUserMenu]);

  const openConfirmModal = useCallback((title, message, action) => {
    pendingConfirmRef.current = action;
    setConfirmModal({ open: true, title, message });
  }, []);

  const executeConfirmModal = useCallback(async () => {
    const action = pendingConfirmRef.current;
    pendingConfirmRef.current = null;
    setConfirmModal({ open: false, title: "", message: "" });
    if (typeof action === "function") {
      await action();
    }
  }, []);
  const [adminPosts, setAdminPosts] = useState([]);
  const [newAdminPost, setNewAdminPost] = useState({
    title: "",
    content: "",
    imageFile: null,
  });
  const [adminPostPreview, setAdminPostPreview] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(false);

  // Cropping state for Admin Post image
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState(null);

  const onCropComplete = (_, areaPixels) => setCroppedAreaPixels(areaPixels);

  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async function getCroppedBlob(imageSrc, pixelCrop) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(pixelCrop.width));
        canvas.height = Math.max(1, Math.round(pixelCrop.height));
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Canvas not supported"));
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          canvas.width,
          canvas.height
        );
        canvas.toBlob(
          (blob) => {
            if (!blob) return reject(new Error("Canvas is empty"));
            resolve(blob);
          },
          "image/jpeg",
          0.92
        );
      };
      image.onerror = reject;
      image.src = imageSrc;
    });
  }

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(USERS_PER_PAGE),
      });
      if (userSearch.trim()) params.set("q", userSearch.trim());
      if (statusFilter) params.set("status", statusFilter);
      if (dateFilter) params.set("date", dateFilter);

      const userRes = await fetch(
        getApiUrl(`/api/admin/users?${params.toString()}`),
        { credentials: "include" }
      );
      const userData = await userRes.json();
      if (Array.isArray(userData)) {
        setUsers(userData);
        setUsersPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: userData.length,
          hasNextPage: false,
        });
      } else {
        setUsers(userData.users || []);
        setUsersPagination({
          currentPage: userData.pagination?.currentPage ?? page,
          totalPages: userData.pagination?.totalPages ?? 1,
          totalItems: userData.pagination?.totalItems ?? 0,
          hasNextPage: Boolean(userData.pagination?.hasNextPage),
        });
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  }, [userSearch, statusFilter, dateFilter]);

  const fetchPoems = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(POEMS_PER_PAGE),
      });
      if (poemSearch.trim()) params.set("q", poemSearch.trim());

      const poemRes = await fetch(
        getApiUrl(`/api/admin/poems?${params.toString()}`),
        { credentials: "include" }
      );
      const poemData = await poemRes.json();
      if (Array.isArray(poemData)) {
        setPoems(poemData);
        setPoemsPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: poemData.length,
          hasNextPage: false,
        });
      } else {
        setPoems(poemData.poems || []);
        setPoemsPagination({
          currentPage: poemData.pagination?.currentPage ?? page,
          totalPages: poemData.pagination?.totalPages ?? 1,
          totalItems: poemData.pagination?.totalItems ?? 0,
          hasNextPage: Boolean(poemData.pagination?.hasNextPage),
        });
      }
    } catch (err) {
      console.error("Error fetching poems:", err);
    }
  }, [poemSearch]);

  const fetchData = async () => {
    await Promise.all([fetchUsers(userPage), fetchPoems(poemPage)]);
  };

  useEffect(() => {
    fetchUsers(userPage);
  }, [fetchUsers, userPage]);

  useEffect(() => {
    fetchPoems(poemPage);
  }, [fetchPoems, poemPage]);

  useEffect(() => {
    // Fetch user growth stats
    const fetchGrowth = async () => {
      const res = await fetch(
        getApiUrl("/api/admin/user-growth-stats"),
        {
          credentials: "include", // Include cookies for authentication
        }
      );
      if (res.ok) {
        setGrowthStats(await res.json());
      }
    };
    fetchGrowth();
    // Fetch post engagement stats
    const fetchEngagement = async () => {
      const res = await fetch(
        getApiUrl("/api/admin/post-engagement-stats"),
        {
          credentials: "include", // Include cookies for authentication
        }
      );
      if (res.ok) {
        setEngagementStats(await res.json());
      }
    };
    fetchEngagement();
    // Fetch active users
    const fetchActive = async () => {
      const res = await fetch(getApiUrl("/api/admin/active-users"), {
        credentials: "include", // Include cookies for authentication
      });
      if (res.ok) {
        setActiveUsers(await res.json());
      }
    };
    fetchActive();
    // Fetch admins/moderators
    const fetchAdminMods = async () => {
      const res = await fetch(
        getApiUrl("/api/admin/admins-moderators"),
        {
          credentials: "include", // Include cookies for authentication
        }
      );
      if (res.ok) {
        setAdminMods(await res.json());
      }
    };
    fetchAdminMods();
    // Fetch announcements
    const fetchAnnouncements = async () => {
      const res = await fetch(getApiUrl("/api/admin/announcements"), {
        credentials: "include", // Include cookies for authentication
      });
      if (res.ok) {
        setAnnouncements(await res.json());
      }
    };
    fetchAnnouncements();
    // Fetch admin posts
    const fetchAdminPosts = async () => {
      try {
        const res = await fetch(getApiUrl("/api/admin/posts"));
        if (res.ok) setAdminPosts(await res.json());
      } catch (e) {
        // ignore
      }
    };
    fetchAdminPosts();
  }, []);

  const handleDeleteUser = async (id) => {
    openConfirmModal(
      "Delete User?",
      "Are you sure you want to delete this user? This action cannot be undone.",
      async () => {
        await fetch(getApiUrl(`/api/admin/users/${id}`), {
          method: "DELETE",
          credentials: "include",
        });
        setModal({
          open: true,
          title: "User Deleted",
          message: "User has been deleted.",
          type: "success",
        });
        fetchData();
      }
    );
  };

  const handleDeletePoem = async (id) => {
    openConfirmModal(
      "Delete Poem?",
      "Delete this poem? This action cannot be undone.",
      async () => {
        await fetch(getApiUrl(`/api/admin/poems/${id}`), {
          method: "DELETE",
          credentials: "include",
        });
        fetchData();
      }
    );
  };

  const handleSuspend = async (id) => {
    try {
      await fetch(getApiUrl(`/api/admin/users/${id}/suspend`), {
        method: "PUT",
        credentials: "include", // Include cookies for authentication
      });
      setModal({
        open: true,
        title: "User Suspended",
        message: "User has been suspended.",
        type: "success",
      });
      fetchData();
    } catch (err) {
      setModal({
        open: true,
        title: "Error",
        message: "Failed to suspend user.",
        type: "error",
      });
    }
  };
  const handleActivate = async (id) => {
    try {
      await fetch(getApiUrl(`/api/admin/users/${id}/activate`), {
        method: "PUT",
        credentials: "include", // Include cookies for authentication
      });
      setModal({
        open: true,
        title: "User Activated",
        message: "User has been activated.",
        type: "success",
      });
      fetchData();
    } catch (err) {
      setModal({
        open: true,
        title: "Error",
        message: "Failed to activate user.",
        type: "error",
      });
    }
  };
  const handleResetPassword = async (id) => {
    try {
      await fetch(
        getApiUrl(`/api/admin/users/${id}/reset-password`),
        {
          method: "POST",
          credentials: "include", // Include cookies for authentication
        }
      );
      setModal({
        open: true,
        title: "Password Reset",
        message: "Password has been reset and emailed to user.",
        type: "success",
      });
    } catch (err) {
      setModal({
        open: true,
        title: "Error",
        message: "Failed to reset password.",
        type: "error",
      });
    }
  };
  const handleForceLogout = async (id) => {
    try {
      await fetch(getApiUrl(`/api/admin/users/${id}/force-logout`), {
        method: "POST",
        credentials: "include", // Include cookies for authentication
      });
      setModal({
        open: true,
        title: "Force Logout",
        message: "User has been forced to logout.",
        type: "success",
      });
    } catch (err) {
      setModal({
        open: true,
        title: "Error",
        message: "Failed to force logout user.",
        type: "error",
      });
    }
  };

  const refreshAdminMods = async () => {
    try {
      const res = await fetch(
        getApiUrl("/api/admin/admins-moderators"),
        { credentials: "include" }
      );
      if (res.ok) setAdminMods(await res.json());
    } catch {
      // ignore
    }
  };

  const handlePromoteAdmin = (targetUser) => {
    openConfirmModal(
      "Make admin?",
      `Promote ${targetUser.username || targetUser.email} to admin? They will have full admin dashboard access.`,
      async () => {
        try {
          const res = await fetch(
            getApiUrl(`/api/admin/users/${targetUser._id}/promote-admin`),
            {
              method: "PUT",
              credentials: "include",
            }
          );
          const data = await res.json().catch(() => ({}));
          if (res.ok) {
            setUsers((prev) =>
              prev.map((u) =>
                String(u._id) === String(targetUser._id)
                  ? { ...u, role: "admin" }
                  : u
              )
            );
            await refreshAdminMods();
            setModal({
              open: true,
              title: "User promoted",
              message: `${targetUser.username || "User"} is now an admin.`,
              type: "success",
            });
          } else {
            setModal({
              open: true,
              title: "Error",
              message: data.error || "Failed to promote user.",
              type: "error",
            });
          }
        } catch {
          setModal({
            open: true,
            title: "Error",
            message: "Failed to promote user.",
            type: "error",
          });
        }
      }
    );
  };

  const handleDemoteAdmin = (targetUser) => {
    openConfirmModal(
      "Demote admin?",
      `Remove admin access from ${targetUser.username || targetUser.email}? They will return to a regular user account.`,
      async () => {
        try {
          const res = await fetch(
            getApiUrl(`/api/admin/users/${targetUser._id}/demote-admin`),
            {
              method: "PUT",
              credentials: "include",
            }
          );
          const data = await res.json().catch(() => ({}));
          if (res.ok) {
            setAdminMods((prev) =>
              prev.filter((u) => String(u._id) !== String(targetUser._id))
            );
            setUsers((prev) =>
              prev.map((u) =>
                String(u._id) === String(targetUser._id)
                  ? { ...u, role: "user" }
                  : u
              )
            );
            setModal({
              open: true,
              title: "Admin demoted",
              message: `${targetUser.username || "User"} is no longer an admin.`,
              type: "success",
            });
          } else {
            setModal({
              open: true,
              title: "Error",
              message: data.error || "Failed to demote admin.",
              type: "error",
            });
          }
        } catch {
          setModal({
            open: true,
            title: "Error",
            message: "Failed to demote admin.",
            type: "error",
          });
        }
      }
    );
  };

  const handleViewProfile = (username) => {
    navigate(`/users/${username}`);
  };

  const handleAdminDeletePoem = async (id) => {
    openConfirmModal(
      "Delete Post?",
      "Delete this post? This action cannot be undone.",
      async () => {
        await fetch(getApiUrl(`/api/admin/poems/${id}/admin`), {
          method: "DELETE",
          credentials: "include",
        });
        setModal({
          open: true,
          title: "Post Deleted",
          message: "Post has been deleted.",
          type: "success",
        });
        fetchData();
      }
    );
  };
  const handleEditPoem = (poem) => {
    setPoemEdit({ open: true, poem, title: poem.title, content: poem.content });
  };
  const handleEditPoemSave = async () => {
    const { poem, title, content } = poemEdit;
    await fetch(getApiUrl(`/api/admin/poems/${poem._id}`), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        // Authentication handled via cookies
      },
      body: JSON.stringify({ title, content }),
    });
    setPoemEdit({ open: false, poem: null, title: "", content: "" });
    setModal({
      open: true,
      title: "Post Updated",
      message: "Post has been updated.",
      type: "success",
    });
    fetchData();
  };
  const handleFeaturePoem = async (poem, featured, highlighted) => {
    await fetch(getApiUrl(`/api/admin/poems/${poem._id}/feature`), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        // Authentication handled via cookies
      },
      body: JSON.stringify({ featured, highlighted }),
    });
    fetchData();
  };
  const handleOpenReports = (poem) => {
    setReportModal({ open: true, poem });
  };
  const handleReportStatus = async (poemId, idx, status) => {
    await fetch(getApiUrl(`/api/admin/reports/${poemId}/${idx}`), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        // Authentication handled via cookies
      },
      body: JSON.stringify({ status }),
    });
    fetchData();
  };

  // Server-paginated users (filters applied via query params)
  const filteredUsers = users;
  const filteredUsersTotalPages = Math.max(1, usersPagination.totalPages || 1);
  const paginatedUsers = users;

  const userStats = useMemo(() => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const activeCount = users.filter((u) => u.status === "active").length;
    const newThisMonth = users.filter(
      (u) => u.createdAt && new Date(u.createdAt) >= monthStart
    ).length;
    const onlineCount = activeUsers?.online?.length ?? 0;
    const total = usersPagination.totalItems || users.length;
    return {
      total,
      active: activeCount,
      newThisMonth,
      online: onlineCount,
      growthPct: 0,
      activePct: total ? Math.round((activeCount / Math.max(users.length, 1)) * 100) : 0,
    };
  }, [users, activeUsers, usersPagination.totalItems]);

  useEffect(() => {
    setUserPage(1);
  }, [userSearch, statusFilter, dateFilter]);

  useEffect(() => {
    setPoemPage(1);
  }, [poemSearch]);

  useEffect(() => {
    if (userPage > filteredUsersTotalPages) {
      setUserPage(filteredUsersTotalPages);
    }
  }, [userPage, filteredUsersTotalPages]);

  const formatUserId = (id, index) =>
    `#USR${String(index + 1 + (userPage - 1) * USERS_PER_PAGE).padStart(3, "0")}`;

  const roleBadgeClass = (role) => {
    const r = (role || "user").toLowerCase();
    if (r === "superadmin" || r === "admin") return "admin-badge--role-admin";
    if (r === "moderator") return "admin-badge--role-moderator";
    return "admin-badge--role-user";
  };

  // Poems: server page + light client filters on the current page
  const filteredPoems = poems.filter((poem) => {
    if (
      !poemFilters.author &&
      !poemFilters.date &&
      !poemFilters.report &&
      !poemFilters.featured
    ) {
      return true;
    }
    const matchesAuthor = poemFilters.author
      ? poem.author?.username
          ?.toLowerCase()
          .includes(poemFilters.author.toLowerCase())
      : true;
    const matchesDate = poemFilters.date
      ? new Date(poem.createdAt).toISOString().slice(0, 10) === poemFilters.date
      : true;
    const matchesReport = poemFilters.report
      ? (poem.reports || []).some((r) => r.status === poemFilters.report)
      : true;
    const matchesFeatured = poemFilters.featured
      ? (poem.featured ? "featured" : poem.highlighted ? "highlighted" : "") ===
        poemFilters.featured
      : true;
    return matchesAuthor && matchesDate && matchesReport && matchesFeatured;
  });

  const poemsTotalPages = Math.max(1, poemsPagination.totalPages || 1);
  // Helper to get a plain text preview from poem content (handles Draft.js or plain text)
  function getPoemPreview(content) {
    try {
      let raw = content;
      if (typeof raw === "string") {
        try {
          raw = JSON.parse(raw);
          if (typeof raw === "string") {
            raw = JSON.parse(raw);
          }
        } catch {}
      }
      if (raw && raw.blocks && typeof raw.entityMap !== "undefined") {
        const contentState = convertFromRaw(raw);
        // Get plain text (not HTML)
        return (
          contentState.getPlainText().slice(0, 100) +
          (contentState.getPlainText().length > 100 ? "..." : "")
        );
      }
      // fallback: plain text
      if (typeof content === "string") {
        return content.slice(0, 100) + (content.length > 100 ? "..." : "");
      }
      return "";
    } catch {
      return typeof content === "string" ? content.slice(0, 100) : "";
    }
  }

  // Helper to format chart data
  function getChartData(stats, label, color) {
    return {
      labels: stats.map((d) => d._id),
      datasets: [
        {
          label,
          data: stats.map((d) => d.count),
          fill: false,
          borderColor: color,
          backgroundColor: color,
          tension: 0.3,
          pointRadius: 3,
        },
      ],
    };
  }

  // Helper to get time ago format
  function getTimeAgo(date) {
    const now = new Date();
    const diffMs = now - date;
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  return (
    <>
      <AdminShell
        activeTab={activeTab}
        onTabChange={setActiveTab}
        user={currentUser}
        headerAction={
          activeTab === "users" ? (
            <button type="button" className="admin-btn-primary">
              <FaPlus aria-hidden="true" />
              Add New User
            </button>
          ) : null
        }
      >
        {/* Dashboard Overview */}
        {activeTab === "dashboard" && (
          <>
            <div className="admin-stats">
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">Total Users</p>
                  <p className="admin-stat-card__value">{userStats.total}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--up">
                    {userStats.growthPct >= 0 ? "+" : ""}
                    {userStats.growthPct}% from last month
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--orange">
                  <FaUsers />
                </div>
              </div>
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">Active Users</p>
                  <p className="admin-stat-card__value">{userStats.active}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--up">
                    {userStats.activePct}% of total users
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--green">
                  <FaCircle />
                </div>
              </div>
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">New This Month</p>
                  <p className="admin-stat-card__value">{userStats.newThisMonth}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--up">
                    +{userStats.newThisMonth} this month
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--blue">
                  <FaUser />
                </div>
              </div>
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">Users Online</p>
                  <p className="admin-stat-card__value">{userStats.online}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--muted">
                    <span className="admin-stat-card__sub-dot" />
                    Right now
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--purple">
                  <FaCircle />
                </div>
              </div>
            </div>
            <div className="admin-section-card">
              <h3 className="text-lg font-bold text-[#f26522] mb-4">
                User Growth Stats
              </h3>
              {growthStats ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Daily */}
                  <div>
                    <div className="font-semibold text-[#f26522] mb-2">
                      Daily (30d)
                    </div>
                    <div className="mb-2 bg-slate-50 rounded p-2">
                      <Line
                        data={getChartData(
                          growthStats.daily,
                          "New Users",
                          "#2563eb"
                        )}
                        options={{
                          plugins: { legend: { display: false } },
                          scales: { x: { ticks: { maxTicksLimit: 7 } } },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        height={180}
                      />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr>
                            <th className="text-left">Date</th>
                            <th className="text-right">New Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          {growthStats.daily.map((d) => (
                            <tr key={d._id}>
                              <td>{d._id}</td>
                              <td className="text-right">{d.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Weekly */}
                  <div>
                    <div className="font-semibold text-[#f26522] mb-2">
                      Weekly (12w)
                    </div>
                    <div className="mb-2 bg-slate-50 rounded p-2">
                      <Line
                        data={getChartData(
                          growthStats.weekly,
                          "New Users",
                          "#059669"
                        )}
                        options={{
                          plugins: { legend: { display: false } },
                          scales: { x: { ticks: { maxTicksLimit: 6 } } },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        height={180}
                      />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr>
                            <th className="text-left">Week</th>
                            <th className="text-right">New Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          {growthStats.weekly.map((d) => (
                            <tr key={d._id}>
                              <td>{d._id}</td>
                              <td className="text-right">{d.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {/* Monthly */}
                  <div>
                    <div className="font-semibold text-[#f26522] mb-2">
                      Monthly (12mo)
                    </div>
                    <div className="mb-2 bg-slate-50 rounded p-2">
                      <Line
                        data={getChartData(
                          growthStats.monthly,
                          "New Users",
                          "#f59e42"
                        )}
                        options={{
                          plugins: { legend: { display: false } },
                          scales: { x: { ticks: { maxTicksLimit: 6 } } },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                        height={180}
                      />
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr>
                            <th className="text-left">Month</th>
                            <th className="text-right">New Users</th>
                          </tr>
                        </thead>
                        <tbody>
                          {growthStats.monthly.map((d) => (
                            <tr key={d._id}>
                              <td>{d._id}</td>
                              <td className="text-right">{d.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-slate-400">
                  Loading user growth stats...
                </div>
              )}
            </div>
            {/* Post Engagement Stats */}
            <div className="mt-10 bg-white rounded-2xl shadow-lg p-6 border-t-4 border-green-400">
              <h3 className="text-lg font-bold text-green-700 mb-4">
                Post Engagement
              </h3>
              {engagementStats ? (
                <div className="overflow-x-auto rounded-xl shadow-lg border border-slate-200">
                  <table className="min-w-full rounded-xl">
                    <thead className="sticky top-0 bg-gradient-to-r from-green-50 to-emerald-50 z-10">
                      <tr className="text-slate-700 text-sm">
                        <th className="py-3 px-4 text-left font-semibold">Title</th>
                        <th className="py-3 px-4 text-left font-semibold">Author</th>
                        <th className="py-3 px-4 text-center font-semibold">
                          <span className="flex items-center justify-center gap-1">
                            <FaHeart className="text-red-500" />
                            Likes
                          </span>
                        </th>
                        <th className="py-3 px-4 text-center font-semibold">
                          <span className="flex items-center justify-center gap-1">
                            <FaComment className="text-[#f26522]" />
                            Comments
                          </span>
                        </th>
                        <th className="py-3 px-4 text-center font-semibold">
                          <span className="flex items-center justify-center gap-1">
                            <FaChartBar className="text-green-600" />
                            Engagement
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Combine most liked and most commented, deduplicate by _id, and calculate engagement score
                        const allPosts = new Map();
                        
                        engagementStats.mostLiked.forEach((p) => {
                          allPosts.set(p._id, {
                            ...p,
                            likesCount: p.likes?.length || 0,
                            commentsCount: p.comments?.length || 0,
                          });
                        });
                        
                        engagementStats.mostCommented.forEach((p) => {
                          const existing = allPosts.get(p._id);
                          if (existing) {
                            existing.commentsCount = p.comments?.length || 0;
                          } else {
                            allPosts.set(p._id, {
                              ...p,
                              likesCount: p.likes?.length || 0,
                              commentsCount: p.comments?.length || 0,
                            });
                          }
                        });
                        
                        // Convert to array, calculate engagement score, and sort
                        const postsArray = Array.from(allPosts.values()).map((p) => ({
                          ...p,
                          engagementScore: (p.likesCount || 0) + (p.commentsCount || 0) * 2, // Comments weighted more
                        }));
                        
                        // Sort by engagement score descending
                        postsArray.sort((a, b) => b.engagementScore - a.engagementScore);
                        
                        return postsArray.slice(0, 10).map((p, idx) => (
                          <tr
                            key={p._id}
                            className={`border-b transition hover:bg-green-50 ${
                              idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                            }`}
                          >
                            <td className="py-3 px-4 font-semibold text-[#333333]">
                              <div className="max-w-xs truncate" title={p.title}>
                                {p.title || "Untitled"}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold text-xs">
                                  {(p.author?.username || "U")[0]?.toUpperCase()}
                                </span>
                                <span className="text-slate-700">
                                  {p.author?.username || "Unknown"}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-red-50 text-red-700 font-semibold text-sm">
                                <FaHeart className="text-sm" />
                                {p.likesCount || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-[#fff5ef] text-[#f26522] font-semibold text-sm">
                                <FaComment className="text-sm" />
                                {p.commentsCount || 0}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <span className="inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-sm shadow-sm">
                                <FaChartBar className="text-sm" />
                                {p.engagementScore}
                              </span>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-slate-400 text-center py-8">
                  Loading post engagement stats...
                </div>
              )}
            </div>
            {/* Active Users */}
            <div className="mt-10 bg-white rounded-2xl shadow-lg p-6 border-t-4 border-[#ffd4bc]">
              <h3 className="text-lg font-bold text-[#f26522] mb-4">
                Active Users
              </h3>
              {activeUsers ? (
                <div className="overflow-x-auto rounded-xl shadow-lg border border-slate-200">
                  <table className="min-w-full rounded-xl">
                    <thead className="sticky top-0 bg-gradient-to-r from-[#fff5ef] to-[#fff5ef] z-10">
                      <tr className="text-slate-700 text-sm">
                        <th className="py-3 px-4 text-left font-semibold">User</th>
                        <th className="py-3 px-4 text-left font-semibold">Name</th>
                        <th className="py-3 px-4 text-center font-semibold">
                          <span className="flex items-center justify-center gap-1">
                            <FaCircle className="text-green-500 text-xs" />
                            Status
                          </span>
                        </th>
                        <th className="py-3 px-4 text-center font-semibold">
                          <span className="flex items-center justify-center gap-1">
                            <FaClock className="text-[#f26522]" />
                            Last Active
                          </span>
                        </th>
                        <th className="py-3 px-4 text-center font-semibold">
                          <span className="flex items-center justify-center gap-1">
                            <FaCalendarAlt className="text-[#f26522]" />
                            Activity
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Combine online and most active users, deduplicate by _id
                        const allUsers = new Map();
                        
                        // Add online users first (they get priority)
                        activeUsers.online.forEach((u) => {
                          allUsers.set(u._id, {
                            ...u,
                            isOnline: true,
                            lastActiveDate: new Date(u.lastActive),
                          });
                        });
                        
                        // Add most active users (merge if already exists)
                        activeUsers.mostActive.forEach((u) => {
                          const existing = allUsers.get(u._id);
                          if (existing) {
                            existing.isMostActive = true;
                          } else {
                            allUsers.set(u._id, {
                              ...u,
                              isOnline: false,
                              isMostActive: true,
                              lastActiveDate: new Date(u.lastActive),
                            });
                          }
                        });
                        
                        // Convert to array and sort by last active (most recent first)
                        const usersArray = Array.from(allUsers.values());
                        usersArray.sort((a, b) => b.lastActiveDate - a.lastActiveDate);
                        
                        return usersArray.slice(0, 15).map((u, idx) => {
                          const isOnline = u.isOnline;
                          const timeAgo = getTimeAgo(u.lastActiveDate);
                          
                          return (
                            <tr
                              key={u._id}
                              className={`border-b transition hover:bg-[#fff5ef] ${
                                idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"
                              }`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shadow-sm ${
                                    isOnline 
                                      ? "bg-gradient-to-br from-green-400 to-emerald-500 text-white ring-2 ring-green-300" 
                                      : "bg-[#fff5ef] text-[#f26522]"
                                  }`}>
                                    {(u.username || "U")[0]?.toUpperCase()}
                                  </span>
                                  <div>
                                    <div className="font-semibold text-[#333333]">
                                      {u.username || "Unknown"}
                                    </div>
                                    {u.email && (
                                      <div className="text-xs text-slate-500">
                                        {u.email}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="text-slate-700">
                                  {u.name || "-"}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {isOnline ? (
                                  <span className="inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 font-semibold text-sm">
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                                    Online
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center justify-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold text-sm">
                                    <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                                    Offline
                                  </span>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex flex-col items-center">
                                  <span className="text-sm font-medium text-slate-700">
                                    {timeAgo}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {u.lastActiveDate.toLocaleString()}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {isOnline && (
                                    <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-[#fff5ef] text-[#f26522] text-xs font-semibold">
                                      Online Now
                                    </span>
                                  )}
                                  {u.isMostActive && (
                                    <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-gradient-to-r from-[#f26522] to-[#fff5ef]0 text-white text-xs font-bold shadow-sm">
                                      Most Active (7d)
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-slate-400 text-center py-8">
                  Loading active users...
                </div>
              )}
            </div>
          </>
        )}

        {/* Users Management */}
        {activeTab === "users" && (
          <>
            <div className="admin-stats">
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">Total Users</p>
                  <p className="admin-stat-card__value">{userStats.total}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--up">
                    {userStats.growthPct >= 0 ? "+" : ""}
                    {userStats.growthPct}% from last month
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--orange">
                  <FaUsers />
                </div>
              </div>
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">Active Users</p>
                  <p className="admin-stat-card__value">{userStats.active}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--up">
                    {userStats.activePct}% of total users
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--green">
                  <FaCircle />
                </div>
              </div>
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">New This Month</p>
                  <p className="admin-stat-card__value">{userStats.newThisMonth}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--up">
                    +{userStats.newThisMonth} this month
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--blue">
                  <FaUser />
                </div>
              </div>
              <div className="admin-stat-card">
                <div>
                  <p className="admin-stat-card__label">Users Online</p>
                  <p className="admin-stat-card__value">{userStats.online}</p>
                  <p className="admin-stat-card__sub admin-stat-card__sub--muted">
                    <span className="admin-stat-card__sub-dot" />
                    Right now
                  </p>
                </div>
                <div className="admin-stat-card__icon admin-stat-card__icon--purple">
                  <FaCircle />
                </div>
              </div>
            </div>

            <div className="admin-panel">
              <div className="admin-filters">
                <input
                  type="text"
                  placeholder="Search by name, pen name or email..."
                  className="admin-filters__search"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
                <select
                  className="admin-filters__select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="suspended">Inactive</option>
                </select>
                <input
                  type="date"
                  className="admin-filters__date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  aria-label="Filter by join date"
                />
                <button type="button" className="admin-filters__btn">
                  <FaSlidersH aria-hidden="true" />
                  Filters
                </button>
              </div>
              <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: "2.5rem" }}>
                      <input type="checkbox" aria-label="Select all users" />
                    </th>
                    <th>User</th>
                    <th>Pen Name</th>
                    <th>Email</th>
                    <th>Join Date</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Posts</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user, idx) => (
                      <tr key={user._id}>
                        <td>
                          <input type="checkbox" aria-label={`Select ${user.username}`} />
                        </td>
                        <td>
                          <div className="admin-user-cell">
                            <span
                              className={`admin-user-avatar admin-user-avatar--${
                                idx % 5
                              }`}
                            >
                              {(user.name || user.username || "?")[0]?.toUpperCase()}
                            </span>
                            <div className="admin-user-meta">
                              <span className="admin-user-name">
                                {user.name || user.username || "—"}
                              </span>
                              <span className="admin-user-id">
                                {formatUserId(user._id, idx)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="admin-table__pen-name">{user.username || "—"}</td>
                        <td className="admin-table__email" title={user.email}>
                          {user.email}
                        </td>
                        <td>
                          {user.createdAt
                            ? new Date(user.createdAt).toLocaleDateString()
                            : "—"}
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${roleBadgeClass(user.role)}`}
                          >
                            {user.role === "superadmin"
                              ? "Superadmin"
                              : user.role || "User"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`admin-badge ${
                              user.status === "active"
                                ? "admin-badge--status-active"
                                : "admin-badge--status-suspended"
                            }`}
                          >
                            {user.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="text-center">
                          {
                            poems.filter((p) => p.author?._id === user._id)
                              .length
                          }
                        </td>
                        <td className="admin-table__actions">
                          <div className="admin-row-actions">
                            <button
                              type="button"
                              className="admin-row-actions__btn"
                              title="View Profile"
                              onClick={() => handleViewProfile(user.username)}
                            >
                              <FaEye />
                            </button>
                            <button
                              type="button"
                              className="admin-row-actions__btn"
                              title={user.status === "active" ? "Suspend" : "Activate"}
                              onClick={() =>
                                user.status === "active"
                                  ? handleSuspend(user._id)
                                  : handleActivate(user._id)
                              }
                            >
                              {user.status === "active" ? (
                                <FaUserSlash aria-hidden="true" />
                              ) : (
                                <FaCheck aria-hidden="true" />
                              )}
                            </button>
                            <button
                              type="button"
                              className="admin-row-actions__btn admin-row-actions__more"
                              title="More actions"
                              data-user-menu-btn={user._id}
                              aria-expanded={openUserMenu?.id === user._id}
                              aria-haspopup="menu"
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const menuWidth = 208; // 13rem
                                const estimatedHeight = 280;
                                const gap = 4;
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const openUp =
                                  spaceBelow < estimatedHeight + gap &&
                                  rect.top > spaceBelow;
                                const right = Math.max(
                                  8,
                                  window.innerWidth - rect.right
                                );
                                const leftEdge = window.innerWidth - right - menuWidth;
                                const safeRight =
                                  leftEdge < 8
                                    ? Math.max(8, window.innerWidth - menuWidth - 8)
                                    : right;

                                setOpenUserMenu((prev) =>
                                  prev?.id === user._id
                                    ? null
                                    : openUp
                                      ? {
                                          id: user._id,
                                          bottom:
                                            window.innerHeight - rect.top + gap,
                                          right: safeRight,
                                          openUp: true,
                                        }
                                      : {
                                          id: user._id,
                                          top: rect.bottom + gap,
                                          right: safeRight,
                                          openUp: false,
                                        }
                                );
                              }}
                            >
                              <FaEllipsisV aria-hidden="true" />
                            </button>
                            {openUserMenu?.id === user._id && (
                              <div
                                ref={userMenuRef}
                                className={`admin-row-actions__menu admin-row-actions__menu--fixed${
                                  openUserMenu.openUp
                                    ? " admin-row-actions__menu--up"
                                    : ""
                                }`}
                                style={
                                  openUserMenu.openUp
                                    ? {
                                        bottom: openUserMenu.bottom,
                                        right: openUserMenu.right,
                                        top: "auto",
                                        maxHeight: `calc(100vh - ${
                                          openUserMenu.bottom + 8
                                        }px)`,
                                      }
                                    : {
                                        top: openUserMenu.top,
                                        right: openUserMenu.right,
                                        maxHeight: `calc(100vh - ${
                                          openUserMenu.top + 8
                                        }px)`,
                                      }
                                }
                                role="menu"
                              >
                                <button
                                  type="button"
                                  className="admin-row-actions__menu-item"
                                  onClick={() => {
                                    setOpenUserMenu(null);
                                    setNotifyModal({
                                      open: true,
                                      user,
                                      message: "",
                                      type: "info",
                                    });
                                  }}
                                >
                                  Notify
                                </button>
                                {isSuperAdmin &&
                                  (user.role === "user" || user.role === "moderator") && (
                                    <button
                                      type="button"
                                      className="admin-row-actions__menu-item"
                                      onClick={() => {
                                        setOpenUserMenu(null);
                                        handlePromoteAdmin(user);
                                      }}
                                    >
                                      Make admin
                                    </button>
                                  )}
                                <button
                                  type="button"
                                  className="admin-row-actions__menu-item"
                                  onClick={() => {
                                    setOpenUserMenu(null);
                                    handleResetPassword(user._id);
                                  }}
                                >
                                  Reset password
                                </button>
                                <button
                                  type="button"
                                  className="admin-row-actions__menu-item"
                                  onClick={() => {
                                    setOpenUserMenu(null);
                                    handleForceLogout(user._id);
                                  }}
                                >
                                  Force logout
                                </button>
                                <button
                                  type="button"
                                  className="admin-row-actions__menu-item admin-row-actions__menu-item--danger"
                                  onClick={() => {
                                    setOpenUserMenu(null);
                                    handleDeleteUser(user._id);
                                  }}
                                >
                                  Delete user
                                </button>
                                <hr className="admin-row-actions__menu-divider" />
                                <label className="admin-row-actions__menu-label">
                                  Badge
                                </label>
                                <select
                                  className="admin-row-actions__menu-select"
                                  value={user.badge || "none"}
                                  onChange={async (e) => {
                                    const badge = e.target.value;
                                    try {
                                      const res = await fetch(
                                        getApiUrl(`/api/admin/users/${user._id}/badge`),
                                        {
                                          method: "PUT",
                                          headers: { "Content-Type": "application/json" },
                                          credentials: "include",
                                          body: JSON.stringify({ badge }),
                                        }
                                      );
                                      if (res.ok) {
                                        setUsers((prev) =>
                                          prev.map((u) =>
                                            u._id === user._id ? { ...u, badge } : u
                                          )
                                        );
                                      } else {
                                        const error = await res.json().catch(() => ({}));
                                        setModal({
                                          open: true,
                                          title: "Error",
                                          message: error.error || "Failed to update badge",
                                          type: "error",
                                        });
                                      }
                                    } catch (err) {
                                      setModal({
                                        open: true,
                                        title: "Error",
                                        message: "Failed to update badge",
                                        type: "error",
                                      });
                                    }
                                  }}
                                >
                                  <option value="none">No Badge</option>
                                  <option value="verified">Verified</option>
                                  <option value="star">Admin</option>
                                </select>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-8 text-[#777]">
                        No users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
              <div className="admin-pagination">
                <span>
                  Showing{" "}
                  {usersPagination.totalItems === 0
                    ? 0
                    : (userPage - 1) * USERS_PER_PAGE + 1}{" "}
                  to{" "}
                  {Math.min(
                    userPage * USERS_PER_PAGE,
                    usersPagination.totalItems || filteredUsers.length
                  )}{" "}
                  of {usersPagination.totalItems || filteredUsers.length} users
                </span>
                <div className="admin-pagination__controls">
                  <button
                    type="button"
                    className="admin-pagination__btn"
                    disabled={userPage <= 1}
                    onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  {Array.from({ length: Math.min(filteredUsersTotalPages, 5) }).map(
                    (_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          type="button"
                          className={`admin-pagination__btn${
                            userPage === page ? " admin-pagination__btn--active" : ""
                          }`}
                          onClick={() => setUserPage(page)}
                        >
                          {page}
                        </button>
                      );
                    }
                  )}
                  <button
                    type="button"
                    className="admin-pagination__btn"
                    disabled={userPage >= filteredUsersTotalPages}
                    onClick={() =>
                      setUserPage((p) => Math.min(filteredUsersTotalPages, p + 1))
                    }
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
            <Modal
              {...modal}
              open={modal.open}
              onClose={() => setModal({ ...modal, open: false })}
            />
          </>
        )}

        {/* Poems Management */}
        {activeTab === "poems" && (
          <section className="admin-section-card">
            <h2 className="text-xl font-semibold mb-6 text-[#f26522] border-l-4 border-[#f26522] pl-3 flex items-center gap-2">
              <FaBook className="text-[#f26522]" /> Posts
            </h2>
            <div className="mb-6 flex flex-col md:flex-row gap-2 md:items-center">
              <input
                type="text"
                placeholder="Search by keyword..."
                className="w-full max-w-xs px-4 py-2 border border-[#eeeeee] rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ffd4bc]"
                value={poemSearch}
                onChange={(e) => setPoemSearch(e.target.value)}
              />
              <input
                type="text"
                placeholder="Author..."
                className="px-3 py-2 border border-[#eeeeee] rounded-lg"
                value={poemFilters.author}
                onChange={(e) =>
                  setPoemFilters((f) => ({ ...f, author: e.target.value }))
                }
              />
              <input
                type="date"
                className="px-3 py-2 border border-[#eeeeee] rounded-lg"
                value={poemFilters.date}
                onChange={(e) =>
                  setPoemFilters((f) => ({ ...f, date: e.target.value }))
                }
              />
              <select
                className="px-3 py-2 border border-[#eeeeee] rounded-lg"
                value={poemFilters.report}
                onChange={(e) =>
                  setPoemFilters((f) => ({ ...f, report: e.target.value }))
                }
              >
                <option value="">All Reports</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="dismissed">Dismissed</option>
              </select>
              <select
                className="px-3 py-2 border border-[#eeeeee] rounded-lg"
                value={poemFilters.featured}
                onChange={(e) =>
                  setPoemFilters((f) => ({ ...f, featured: e.target.value }))
                }
              >
                <option value="">All</option>
                <option value="featured">Featured</option>
                <option value="highlighted">Highlighted</option>
              </select>
            </div>
            <div className="overflow-x-auto rounded-xl shadow-lg border border-slate-200 bg-white">
              <table className="min-w-full rounded-xl">
                <thead className="sticky top-0 bg-slate-100 z-10">
                  <tr className="text-slate-700 text-sm">
                    <th className="py-3 px-3">Title</th>
                    <th className="py-3 px-3">Author</th>
                    <th className="py-3 px-3">Date</th>
                    <th className="py-3 px-3">Preview</th>
                    <th className="py-3 px-3">Likes</th>
                    <th className="py-3 px-3">Comments</th>
                    <th className="py-3 px-3">Reports</th>
                    <th className="py-3 px-3">Featured</th>
                    <th className="py-3 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPoems.length > 0 ? (
                    filteredPoems.map((poem, idx) => (
                      <tr
                        key={poem._id}
                        className={`border-b ${
                          idx % 2 === 0 ? "bg-white" : "bg-slate-50"
                        } hover:bg-[#fff5ef] transition`}
                      >
                        <td className="py-2 px-3 font-semibold text-[#f26522]">
                          {poem.title}
                        </td>
                        <td className="py-2 px-3">
                          {poem.author?.username || "Unknown"}
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {new Date(poem.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-2 px-3 text-xs max-w-xs truncate">
                          {getPoemPreview(poem.content)}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {poem.likes?.length || 0}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {poem.comments?.length || 0}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {poem.reports?.length > 0 ? (
                            <button
                              className="text-xs bg-[#f26522] bg-opacity-10 text-[#f26522] px-2 py-1 rounded hover:bg-[#f26522] hover:bg-opacity-20 font-semibold"
                              onClick={() => handleOpenReports(poem)}
                            >
                              {poem.reports.filter(
                                (r) => r.status === "pending"
                              ).length > 0 ? (
                                <span className="animate-pulse">
                                  {
                                    poem.reports.filter(
                                      (r) => r.status === "pending"
                                    ).length
                                  }{" "}
                                  Pending
                                </span>
                              ) : (
                                <span>{poem.reports.length} Reports</span>
                              )}
                            </button>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          <button
                            className={`text-xs px-2 py-1 rounded font-semibold mr-1 ${
                              poem.featured
                                ? "bg-yellow-200 text-yellow-800"
                                : "bg-slate-100 text-slate-500"
                            }`}
                            title="Toggle Featured"
                            onClick={() =>
                              handleFeaturePoem(
                                poem,
                                !poem.featured,
                                poem.highlighted
                              )
                            }
                          >
                            {poem.featured ? "Featured" : "Feature"}
                          </button>
                          <button
                            className={`text-xs px-2 py-1 rounded font-semibold ${
                              poem.highlighted
                                ? "bg-[#ffd4bc] text-[#d95518]"
                                : "bg-slate-100 text-slate-500"
                            }`}
                            title="Toggle Highlighted"
                            onClick={() =>
                              handleFeaturePoem(
                                poem,
                                poem.featured,
                                !poem.highlighted
                              )
                            }
                          >
                            {poem.highlighted ? "Highlighted" : "Highlight"}
                          </button>
                        </td>
                        <td className="py-2 px-3 flex flex-wrap gap-1">
                          <button
                            className="group text-xs bg-[#fff5ef] text-[#f26522] px-2 py-1 rounded hover:bg-[#ffd4bc] flex items-center gap-1 transition"
                            title="Edit"
                            onClick={() => handleEditPoem(poem)}
                          >
                            <FaSync className="group-hover:rotate-90 transition-transform" />
                          </button>
                          <button
                            className="group text-xs bg-[#f26522] bg-opacity-10 text-[#f26522] px-2 py-1 rounded hover:bg-[#f26522] hover:bg-opacity-20 flex items-center gap-1 transition"
                            title="Delete"
                            onClick={() => handleAdminDeletePoem(poem._id)}
                          >
                            <FaTrash className="group-hover:scale-110" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={9}
                        className="text-slate-400 text-center py-4"
                      >
                        No posts found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="admin-pagination">
              <span>
                Showing{" "}
                {poemsPagination.totalItems === 0
                  ? 0
                  : (poemPage - 1) * POEMS_PER_PAGE + 1}{" "}
                to{" "}
                {Math.min(
                  poemPage * POEMS_PER_PAGE,
                  poemsPagination.totalItems
                )}{" "}
                of {poemsPagination.totalItems} poems
              </span>
              <div className="admin-pagination__controls">
                <button
                  type="button"
                  className="admin-pagination__btn"
                  disabled={poemPage <= 1}
                  onClick={() => setPoemPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                {Array.from({ length: Math.min(poemsTotalPages, 5) }).map(
                  (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        type="button"
                        className={`admin-pagination__btn${
                          poemPage === page ? " admin-pagination__btn--active" : ""
                        }`}
                        onClick={() => setPoemPage(page)}
                      >
                        {page}
                      </button>
                    );
                  }
                )}
                <button
                  type="button"
                  className="admin-pagination__btn"
                  disabled={poemPage >= poemsTotalPages}
                  onClick={() =>
                    setPoemPage((p) => Math.min(poemsTotalPages, p + 1))
                  }
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>
            </div>
            {/* Edit Poem Modal */}
            {poemEdit.open && (
              <Modal
                open={poemEdit.open}
                title="Edit Post"
                message={null}
                type="info"
                onClose={() =>
                  setPoemEdit({
                    open: false,
                    poem: null,
                    title: "",
                    content: "",
                  })
                }
                onConfirm={handleEditPoemSave}
                confirmText="Save"
              >
                <div className="flex flex-col gap-2 mt-2">
                  <input
                    type="text"
                    className="border p-2 rounded"
                    value={poemEdit.title}
                    onChange={(e) =>
                      setPoemEdit((pe) => ({ ...pe, title: e.target.value }))
                    }
                    placeholder="Title"
                  />
                  <textarea
                    ref={editContentRef}
                    className="border p-2 rounded min-h-[100px]"
                    value={poemEdit.content}
                    onChange={(e) =>
                      setPoemEdit((pe) => ({ ...pe, content: e.target.value }))
                    }
                    placeholder="Content"
                  />
                </div>
              </Modal>
            )}
            {/* Reports Modal */}
            {reportModal.open && (
              <Modal
                open={reportModal.open}
                title="Reports"
                message={null}
                type="info"
                onClose={() => setReportModal({ open: false, poem: null })}
              >
                <div className="max-h-96 overflow-y-auto">
                  {reportModal.poem?.reports?.length > 0 ? (
                    reportModal.poem.reports.map((r, idx) => (
                      <div
                        key={idx}
                        className="border-b py-2 flex flex-col gap-1"
                      >
                        <div className="text-sm">
                          <b>Reason:</b> {r.reason || "-"}
                        </div>
                        <div className="text-xs text-slate-500">
                          By: {r.reportedBy?.username || r.reportedBy} on{" "}
                          {new Date(r.date).toLocaleDateString()}
                        </div>
                        <div className="flex gap-2 mt-1">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              r.status === "pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : r.status === "reviewed"
                                ? "bg-green-100 text-green-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {r.status}
                          </span>
                          {r.status === "pending" && (
                            <>
                              <button
                                className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200 font-semibold"
                                onClick={() =>
                                  handleReportStatus(
                                    reportModal.poem._id,
                                    idx,
                                    "reviewed"
                                  )
                                }
                              >
                                Mark Reviewed
                              </button>
                              <button
                                className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded hover:bg-slate-200 font-semibold"
                                onClick={() =>
                                  handleReportStatus(
                                    reportModal.poem._id,
                                    idx,
                                    "dismissed"
                                  )
                                }
                              >
                                Dismiss
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-400">
                      No reports for this post.
                    </div>
                  )}
                </div>
              </Modal>
            )}
            <Modal
              {...modal}
              open={modal.open}
              onClose={() => setModal({ ...modal, open: false })}
            />
          </section>
        )}

        {/* Announcements Management */}
        {activeTab === "announcements" && (
          <section className="admin-section-card">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#333333] flex items-center gap-3">
                <FaBullhorn className="text-[#f26522]" />
                Announcements
              </h2>
              <div className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                {announcements.length} {announcements.length === 1 ? "announcement" : "announcements"}
              </div>
            </div>

            {/* Create Announcement Form */}
            <div className="mb-8 bg-gradient-to-br from-[#fff5ef] to-[#fff5ef] p-6 rounded-2xl shadow-lg border border-[#eeeeee]">
              <div className="flex items-center gap-2 mb-4">
                <FaPlus className="text-[#f26522]" />
                <h3 className="text-lg font-semibold text-[#333333]">Create New Announcement</h3>
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border border-[#eeeeee] focus:ring-2 focus:ring-[#ffd4bc] focus:border-[#f26522] transition-all bg-white text-[#333333] placeholder-[#777777]"
                    placeholder="Enter announcement title"
                    value={newAnnouncement.title}
                    onChange={(e) =>
                      setNewAnnouncement((a) => ({ ...a, title: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border border-[#eeeeee] focus:ring-2 focus:ring-[#ffd4bc] focus:border-[#f26522] transition-all bg-white text-[#333333] placeholder-[#777777] min-h-[100px] resize-y"
                    placeholder="Enter announcement message"
                    value={newAnnouncement.message}
                    onChange={(e) =>
                      setNewAnnouncement((a) => ({ ...a, message: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <FaCalendarAlt className="text-[#f26522]" />
                    Expiration Date (Optional)
                  </label>
                  <input
                    type="date"
                    className="w-full px-4 py-3 rounded-xl border border-[#eeeeee] focus:ring-2 focus:ring-[#ffd4bc] focus:border-[#f26522] transition-all bg-white text-[#333333]"
                    value={newAnnouncement.expiresAt}
                    onChange={(e) =>
                      setNewAnnouncement((a) => ({
                        ...a,
                        expiresAt: e.target.value,
                      }))
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Leave empty for announcements that never expire
                  </p>
                </div>
                <button
                  className="group w-full sm:w-auto bg-gradient-to-r from-[#f26522] to-[#d95518] text-white px-6 py-3 rounded-xl font-semibold hover:from-[#d95518] hover:to-[#c44d15] shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  onClick={async () => {
                    try {
                      const createRes = await fetch(
                        getApiUrl("/api/admin/announcements"),
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                          },
                          credentials: "include",
                          body: JSON.stringify(newAnnouncement),
                        }
                      );
                      if (!createRes.ok) {
                        const errorData = await createRes.json().catch(() => ({}));
                        toast.error(
                          errorData.error ||
                            errorData.msg ||
                            "Failed to create announcement",
                          {
                            title: "Error",
                          }
                        );
                        return;
                      }
                      setNewAnnouncement({ title: "", message: "", expiresAt: "" });
                      const res = await fetch(
                        getApiUrl("/api/admin/announcements"),
                        {
                          credentials: "include",
                        }
                      );
                      if (res.ok) {
                        setAnnouncements(await res.json());
                        toast.success("Announcement created successfully", {
                          title: "Success",
                        });
                      }
                    } catch (err) {
                      toast.error("Failed to create announcement: " + err.message, {
                        title: "Error",
                      });
                    }
                  }}
                  disabled={!newAnnouncement.title || !newAnnouncement.message}
                >
                  <FaPlus className="group-hover:rotate-90 transition-transform duration-200" />
                  Create Announcement
                </button>
              </div>
            </div>

            {/* Announcements List */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-gradient-to-r from-[#fff5ef] to-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Title
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Message
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Expires
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {announcements.length > 0 ? (
                      announcements.map((a) => {
                        const isExpired = a.expiresAt && new Date(a.expiresAt) < new Date();
                        return (
                          <tr
                            key={a._id}
                            className="hover:bg-[#fff5ef] transition-colors duration-150"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <FaBullhorn className="text-[#f26522] text-sm" />
                                <span className="text-sm font-semibold text-slate-900">
                                  {a.title}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <p className="text-sm text-slate-700 max-w-xs truncate">
                                {a.message}
                              </p>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <FaUserCircle className="text-slate-400 text-sm" />
                                <span className="text-sm text-slate-600">
                                  {a.createdBy?.username || "-"}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2 text-sm text-slate-600">
                                <FaClock className="text-slate-400" />
                                <span>{new Date(a.createdAt).toLocaleString()}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {a.expiresAt ? (
                                <div className={`flex items-center gap-2 text-sm ${isExpired ? 'text-red-600' : 'text-slate-600'}`}>
                                  <FaCalendarAlt className={isExpired ? 'text-red-500' : 'text-slate-400'} />
                                  <span className={isExpired ? 'font-semibold' : ''}>
                                    {new Date(a.expiresAt).toLocaleDateString()}
                                    {isExpired && (
                                      <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                        Expired
                                      </span>
                                    )}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-sm text-slate-400 italic">Never expires</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <button
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-150"
                                onClick={() => {
                                  openConfirmModal(
                                    "Delete Announcement?",
                                    "Are you sure you want to delete this announcement? This action cannot be undone.",
                                    async () => {
                                      try {
                                        const res = await fetch(
                                          getApiUrl(`/api/admin/announcements/${a._id}`),
                                          {
                                            method: "DELETE",
                                            credentials: "include",
                                          }
                                        );
                                        if (res.ok) {
                                          setAnnouncements((prev) =>
                                            prev.filter((x) => x._id !== a._id)
                                          );
                                          toast.success("Announcement deleted successfully", {
                                            title: "Success",
                                          });
                                        } else {
                                          toast.error("Failed to delete announcement", {
                                            title: "Error",
                                          });
                                        }
                                      } catch (err) {
                                        toast.error(
                                          "Failed to delete announcement: " + err.message,
                                          {
                                            title: "Error",
                                          }
                                        );
                                      }
                                    }
                                  );
                                }}
                              >
                                <FaTrash />
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <FaBullhorn className="text-4xl text-slate-300" />
                            <p className="text-slate-500 font-medium">No announcements found</p>
                            <p className="text-sm text-slate-400">
                              Create your first announcement to get started
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Admin Posts */}
        {activeTab === "adminposts" && (
          <section className="admin-section-card">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#333333] flex items-center gap-3">
                <FaNewspaper className="text-[#f26522]" />
                Admin Posts
              </h2>
              <div className="text-sm text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                {adminPosts.length} {adminPosts.length === 1 ? "post" : "posts"}
              </div>
            </div>

            {/* Create Admin Post */}
            <div className="mb-8 bg-gradient-to-br from-[#fff5ef] to-[#fff5ef] p-6 rounded-2xl shadow-lg border border-[#eeeeee]">
              <div className="flex items-center gap-2 mb-4">
                <FaPlus className="text-[#f26522]" />
                <h3 className="text-lg font-semibold text-[#333333]">Create New Admin Post</h3>
              </div>
              <div className="flex flex-col gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <FaNewspaper className="text-[#f26522]" />
                    Title <span className="text-slate-400 text-xs font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border border-[#eeeeee] focus:ring-2 focus:ring-[#f26522] focus:border-[#f26522] transition-all bg-white text-[#333333] placeholder-[#777777]"
                    placeholder="Enter post title (optional)"
                    value={newAdminPost.title}
                    onChange={(e) =>
                      setNewAdminPost((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    className="w-full px-4 py-3 rounded-xl border border-[#eeeeee] focus:ring-2 focus:ring-[#f26522] focus:border-[#f26522] transition-all bg-white text-[#333333] placeholder-[#777777] min-h-[120px] resize-y"
                    placeholder="Write your announcement content for the special admin post..."
                    value={newAdminPost.content}
                    onChange={(e) =>
                      setNewAdminPost((p) => ({ ...p, content: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <FaImage className="text-[#f26522]" />
                    Optional Image
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        // Validate file type on client side
                        const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];
                        if (!allowedTypes.includes(file.type)) {
                          toast.error("Invalid file type. Image must be an image file (JPEG, PNG, GIF, WebP).", {
                            title: "Invalid File",
                          });
                          // Clear the file input
                          e.target.value = "";
                          return;
                        }
                        
                        setIsLoadingImage(true);
                        try {
                          const dataUrl = await readFileAsDataURL(file);
                          setCropImageSrc(dataUrl);
                          setIsCropping(true);
                          setCrop({ x: 0, y: 0 });
                          setZoom(1);
                          setCroppedAreaPixels(null);
                        } catch (err) {
                          toast.error("Failed to load image: " + err.message, {
                            title: "Error",
                          });
                        } finally {
                          setIsLoadingImage(false);
                        }
                      }}
                      disabled={isLoadingImage}
                      className="w-full px-4 py-3 rounded-xl border border-[#eeeeee] focus:ring-2 focus:ring-[#f26522] focus:border-[#f26522] transition-all bg-white text-[#333333] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#fff5ef] file:text-[#f26522] hover:file:bg-[#fff5ef] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    {isLoadingImage && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm rounded-xl">
                        <div className="flex flex-col items-center gap-2">
                          <FaSpinner className="text-2xl text-[#f26522] animate-spin" />
                          <p className="text-sm text-slate-600 font-medium">Loading image...</p>
                        </div>
                      </div>
                    )}
                  </div>
                  {adminPostPreview && (
                    <div className="mt-3 p-3 bg-white rounded-xl border border-slate-200">
                      <p className="text-xs text-slate-600 mb-2 font-medium">Image Preview:</p>
                      <img
                        src={adminPostPreview}
                        alt="preview"
                        className="max-h-48 w-full object-contain rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setNewAdminPost((p) => ({ ...p, imageFile: null }));
                          if (adminPostPreview) {
                            try {
                              URL.revokeObjectURL(adminPostPreview);
                            } catch {}
                            setAdminPostPreview(null);
                          }
                        }}
                        className="mt-2 text-xs text-red-600 hover:text-red-700 font-medium"
                      >
                        Remove Image
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className="group w-full sm:w-auto bg-gradient-to-r from-[#f26522] to-[#d95518] text-white px-6 py-3 rounded-xl font-semibold hover:from-[#d95518] hover:to-[#c44d15] shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  onClick={async () => {
                    if (!newAdminPost.content.trim()) return;
                    try {
                      const form = new FormData();
                      if (newAdminPost.title)
                        form.append("title", newAdminPost.title);
                      form.append("content", newAdminPost.content);
                      if (newAdminPost.imageFile)
                        form.append("image", newAdminPost.imageFile);
                      const res = await fetch(
                        getApiUrl("/api/admin/posts"),
                        {
                          method: "POST",
                          credentials: "include",
                          body: form,
                        }
                      );
                      if (res.ok) {
                        const created = await res.json();
                        setAdminPosts((prev) => [created, ...prev]);
                        setNewAdminPost({
                          title: "",
                          content: "",
                          imageFile: null,
                        });
                        if (adminPostPreview) {
                          try {
                            URL.revokeObjectURL(adminPostPreview);
                          } catch {}
                          setAdminPostPreview(null);
                        }
                        setModal({
                          open: true,
                          title: "Success",
                          message: "Your admin post has been published successfully.",
                          type: "success",
                        });
                      } else {
                        let errorData;
                        try {
                          errorData = await res.json();
                        } catch (jsonError) {
                          // If response is not valid JSON, show a generic error
                          errorData = {
                            error: res.status === 400 
                              ? "Invalid file type. Please upload an image file (JPEG, PNG, GIF, WebP)."
                              : "Failed to create admin post. Please try again."
                          };
                        }
                        
                        const errorMessage = errorData.error || errorData.message || errorData.msg || "Failed to create admin post.";
                        const isInvalidFileType = /invalid\s*file\s*type|must\s*be\s*an\s*image/i.test(errorMessage);
                        
                        // Clear invalid file if file type error
                        if (isInvalidFileType && newAdminPost.imageFile) {
                          setNewAdminPost((p) => ({ ...p, imageFile: null }));
                          if (adminPostPreview) {
                            try {
                              URL.revokeObjectURL(adminPostPreview);
                            } catch {}
                            setAdminPostPreview(null);
                          }
                        }
                        
                        setModal({
                          open: true,
                          title: "Error",
                          message: errorMessage,
                          type: "error",
                        });
                      }
                    } catch (err) {
                      setModal({
                        open: true,
                        title: "Error",
                        message: "Failed to create admin post: " + err.message,
                        type: "error",
                      });
                    }
                  }}
                  disabled={!newAdminPost.content.trim()}
                >
                  <FaPlus className="group-hover:rotate-90 transition-transform duration-200" />
                  Publish Admin Post
                </button>
              </div>
            </div>
            {isCropping && (
              <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-[#333333] flex items-center gap-2">
                      <FaImage className="text-[#f26522]" />
                      Crop Image
                    </h3>
                    <button
                      onClick={() => {
                        setIsCropping(false);
                        setCropImageSrc(null);
                      }}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                    >
                      <FaTimes className="text-xl" />
                    </button>
                  </div>
                  <div className="relative w-full h-[60vh] bg-slate-900 rounded-xl overflow-hidden border-2 border-slate-200">
                    <Cropper
                      image={cropImageSrc}
                      crop={crop}
                      zoom={zoom}
                      aspect={16 / 9}
                      onCropChange={setCrop}
                      onZoomChange={setZoom}
                      onCropComplete={onCropComplete}
                      showGrid={false}
                    />
                  </div>
                  <div className="mt-4 flex items-center gap-4">
                    <label className="text-sm font-medium text-slate-700 whitespace-nowrap">
                      Zoom:
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      step={0.01}
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#f26522]"
                    />
                    <span className="text-sm text-slate-600 font-medium min-w-[3rem] text-right">
                      {Math.round(zoom * 100)}%
                    </span>
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      className="px-5 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-[#333333] font-medium transition-colors duration-200"
                      onClick={() => {
                        setIsCropping(false);
                        setCropImageSrc(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#f26522] to-[#d95518] hover:from-[#d95518] hover:to-[#c44d15] text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2"
                      onClick={async () => {
                        try {
                          if (!cropImageSrc || !croppedAreaPixels) return;
                          const blob = await getCroppedBlob(
                            cropImageSrc,
                            croppedAreaPixels
                          );
                          const originalFile = new File(
                            [blob],
                            `admin_${Date.now()}.jpg`,
                            {
                              type: "image/jpeg",
                            }
                          );

                          // Compress the image
                          let compressedFile;
                          try {
                            compressedFile = await compressImageUpload(
                              originalFile
                            );
                          } catch (compressionError) {
                            console.warn(
                              "Compression failed, using original:",
                              compressionError
                            );
                            compressedFile = originalFile;
                          }

                          setNewAdminPost((p) => ({
                            ...p,
                            imageFile: compressedFile,
                          }));
                          try {
                            if (adminPostPreview)
                              URL.revokeObjectURL(adminPostPreview);
                          } catch {}
                          const url = URL.createObjectURL(compressedFile);
                          setAdminPostPreview(url);
                          setIsCropping(false);
                          setCropImageSrc(null);
                        } catch {}
                      }}
                    >
                      <FaCheck />
                      Crop & Use
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Posts List */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-gradient-to-r from-[#fff5ef] to-white">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Image
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Content
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Created By
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Created At
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {adminPosts.length > 0 ? (
                      adminPosts.map((p) => (
                        <tr
                          key={p._id}
                          className="hover:bg-[#fff5ef] transition-colors duration-150"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            {p.image ? (
                              <div className="relative group">
                                <img
                                  src={getApiUrl(`${p.image}`)}
                                  alt="admin post"
                                  className="h-20 w-32 object-cover rounded-lg border-2 border-slate-200 shadow-sm group-hover:shadow-md transition-shadow"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 rounded-lg transition-colors" />
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-slate-400">
                                <FaFileImage className="text-lg" />
                                <span className="text-sm italic">No image</span>
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="max-w-md">
                              {p.title && (
                                <h4 className="text-sm font-semibold text-slate-900 mb-1">
                                  {p.title}
                                </h4>
                              )}
                              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words line-clamp-3">
                                {p.content}
                              </p>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <FaUserCircle className="text-slate-400 text-sm" />
                              <span className="text-sm text-slate-600">
                                {p.createdBy?.username || "-"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <FaClock className="text-slate-400" />
                              <span>
                                {new Date(p.createdAt).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric',
                                })}{' '}
                                {new Date(p.createdAt).toLocaleTimeString('en-US', {
                                  hour: 'numeric',
                                  minute: '2-digit',
                                  hour12: true,
                                })}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <button
                              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors duration-150"
                              onClick={() =>
                                openConfirmModal(
                                  "Delete Admin Post?",
                                  "Are you sure you want to delete this admin post? This action cannot be undone.",
                                  async () => {
                                    try {
                                      const res = await fetch(
                                        getApiUrl(`/api/admin/posts/${p._id}`),
                                        {
                                          method: "DELETE",
                                          credentials: "include",
                                        }
                                      );
                                      if (res.ok) {
                                        setAdminPosts((prev) =>
                                          prev.filter((x) => x._id !== p._id)
                                        );
                                        setModal({
                                          open: true,
                                          title: "Success",
                                          message: "Admin post deleted successfully",
                                          type: "success",
                                        });
                                      } else {
                                        setModal({
                                          open: true,
                                          title: "Error",
                                          message: "Failed to delete admin post",
                                          type: "error",
                                        });
                                      }
                                    } catch (err) {
                                      setModal({
                                        open: true,
                                        title: "Error",
                                        message: "Failed to delete admin post: " + err.message,
                                        type: "error",
                                      });
                                    }
                                  }
                                )
                              }
                            >
                              <FaTrash />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <FaNewspaper className="text-4xl text-slate-300" />
                            <p className="text-slate-500 font-medium">No admin posts found</p>
                            <p className="text-sm text-slate-400">
                              Create your first admin post to get started
                            </p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <Modal
              {...modal}
              open={modal.open}
              onClose={() => setModal({ ...modal, open: false })}
            />
          </section>
        )}

        {/* Admins/Moderators Management */}
        {activeTab === "adminmods" && (
          <section className="admin-section-card">
            <h2 className="text-xl font-semibold mb-2 text-[#f26522] border-l-4 border-[#f26522] pl-3 flex items-center gap-2">
              <FaUserShield className="text-[#f26522]" /> Admins & Moderators
            </h2>
            <p className="text-sm text-slate-500 mb-4">
              {isSuperAdmin
                ? "Promote users from the Users tab with Make admin. Demote admins or moderators below."
                : "View admins and moderators. Only a superadmin can promote or demote admins."}
            </p>
            <div className="overflow-x-auto rounded-xl shadow-lg border border-slate-200 bg-white">
              <table className="min-w-full rounded-xl table-fixed">
                <thead className="sticky top-0 bg-slate-100 z-10">
                  <tr className="text-slate-700 text-sm">
                    <th className="py-3 px-3">Pen name</th>
                    <th className="py-3 px-3">Name</th>
                    <th className="py-3 px-3 w-[18rem]">Email</th>
                    <th className="py-3 px-3">Role</th>
                    <th className="py-3 px-3">Status</th>
                    <th className="py-3 px-3">Join Date</th>
                    <th className="py-3 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminMods.length > 0 ? (
                    adminMods.map((user) => (
                      <tr
                        key={user._id}
                        className="border-b hover:bg-[#fff5ef] transition"
                      >
                        <td className="py-2 px-3">{user.username}</td>
                        <td className="py-2 px-3">{user.name || "-"}</td>
                        <td className="py-2 px-3">
                          <span
                            className="block whitespace-normal break-words overflow-hidden"
                            title={user.email}
                          >
                            {user.email}
                          </span>
                        </td>
                        <td className="py-2 px-3 font-semibold capitalize">
                          {user.role}
                        </td>
                        <td className="py-2 px-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold ${
                              user.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-yellow-100 text-yellow-700"
                            }`}
                          >
                            {user.status}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {new Date(user.createdAt).toLocaleDateString()}
                        </td>
                        <td className="py-2 px-3 flex flex-wrap gap-1">
                          {/* promotion buttons removed */}
                          {user.role === "moderator" && (
                            <>
                              <button
                                className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded hover:bg-yellow-200"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(
                                      getApiUrl(`/api/admin/users/${user._id}/demote-moderator`),
                                      {
                                        method: "PUT",
                                        credentials: "include",
                                      }
                                    );
                                    if (res.ok) {
                                      setAdminMods((prev) =>
                                        prev.map((u) =>
                                          u._id === user._id
                                            ? { ...u, role: "user" }
                                            : u
                                        )
                                      );
                                    }
                                  } catch {}
                                }}
                              >
                                Demote to User
                              </button>
                              <button
                                className="text-xs bg-[#f26522] bg-opacity-10 text-[#f26522] px-2 py-1 rounded hover:bg-[#f26522] hover:bg-opacity-20"
                                onClick={async () => {
                                  try {
                                    const res = await fetch(
                                      getApiUrl(`/api/admin/users/${user._id}/remove-moderator`),
                                      {
                                        method: "PUT",
                                        credentials: "include",
                                      }
                                    );
                                    if (res.ok) {
                                      setAdminMods((prev) =>
                                        prev.filter((u) => u._id !== user._id)
                                      );
                                    }
                                  } catch {}
                                }}
                              >
                                Remove Moderator
                              </button>
                            </>
                          )}
                          {user.role === "admin" && (
                            <>
                              {String(currentUser?._id) !== String(user._id) ? (
                                <button
                                  type="button"
                                  className="text-xs bg-rose-100 text-rose-700 px-2 py-1 rounded hover:bg-rose-200"
                                  onClick={() => handleDemoteAdmin(user)}
                                >
                                  Demote admin
                                </button>
                              ) : (
                                <span className="text-xs text-slate-400">You</span>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="text-slate-400 text-center py-4"
                      >
                        No admins or moderators found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </AdminShell>

      {/* Confirm Modal */}
      {confirmModal.open && (
        <Modal
          open={confirmModal.open}
          title={confirmModal.title || "Confirm Action"}
          message={confirmModal.message || "Are you sure?"}
          type="error"
          onClose={closeConfirmModal}
          onConfirm={executeConfirmModal}
          confirmText="Confirm"
        />
      )}
      {/* Notify Modal */}
      {notifyModal.open && notifyModal.user && (
        <Modal
          key={`notify-modal-${notifyModal.user._id}`}
          open={notifyModal.open}
          title={`Send Notification to ${notifyModal.user.username}`}
          message={null}
          type={notifyModal.type}
          onClose={() =>
            setNotifyModal({
              open: false,
              user: null,
              message: "",
              type: "info",
            })
          }
          onConfirm={async () => {
            try {
              const res = await fetch(
                getApiUrl(`/api/admin/users/${notifyModal.user._id}/notify`),
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  credentials: "include",
                  body: JSON.stringify({
                    message: notifyModal.message,
                    type: notifyModal.type,
                  }),
                }
              );
              if (res.ok) {
                setNotifyModal({
                  open: false,
                  user: null,
                  message: "",
                  type: "info",
                });
                setModal({
                  open: true,
                  title: "Notification Sent",
                  message: "Notification sent to user.",
                  type: "success",
                });
              } else {
                const error = await res.json().catch(() => ({}));
                setModal({
                  open: true,
                  title: "Error",
                  message: error.error || "Failed to send notification",
                  type: "error",
                });
              }
            } catch (err) {
              setModal({
                open: true,
                title: "Error",
                message: "Failed to send notification",
                type: "error",
              });
            }
          }}
          confirmText="Send"
        >
          <div className="flex flex-col gap-2 mt-2">
            <input
              ref={notificationInputRef}
              type="text"
              className="border p-2 rounded"
              value={notifyModal.message}
              onChange={(e) => {
                const value = e.target.value;
                setNotifyModal((n) => ({ ...n, message: value }));
                // Maintain focus after state update
                requestAnimationFrame(() => {
                  if (notificationInputRef.current && document.activeElement !== notificationInputRef.current) {
                    notificationInputRef.current.focus();
                    // Set cursor position to end of text
                    const len = value.length;
                    notificationInputRef.current.setSelectionRange(len, len);
                  }
                });
              }}
              onFocus={(e) => {
                // Ensure cursor is at the end when focused
                const len = e.target.value.length;
                e.target.setSelectionRange(len, len);
              }}
              placeholder="Notification message"
              autoFocus
            />
            <select
              className="border p-2 rounded"
              value={notifyModal.type}
              onChange={(e) =>
                setNotifyModal((n) => ({ ...n, type: e.target.value }))
              }
            >
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
          </div>
        </Modal>
      )}

    </>
  );
}

export default AdminDashboard;
